import { useState } from "react";
import { Button, cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { i18n } from "@tellescope/i18n";
import {
  Plus,
  Trash2,
  ChevronDown,
  GripVertical,
  Type,
  Heading,
  MousePointerClick,
  Square,
  Space,
  Minus,
  Megaphone,
  Code,
  Calendar,
  CalendarPlus,
  Package,
  MessageCircle,
  MessageSquare,
  FileText,
  ClipboardList,
  NotebookPen,
  LayoutList,
  UserPen,
  BookOpen,
  CreditCard,
  Upload,
  Zap,
  Activity,
  Frame,
  Receipt,
} from "lucide-react";
import type { Block, BlockType, BlockUpdate } from "../../../shared/schema";
import { BlockEditor } from "./BlockEditor";
import { BlockTypeSelector } from "./BlockTypeSelector";

interface BlockListProps {
  blocks: Block[];
  onAddBlock: (type: BlockType) => void;
  onUpdateBlock: <T extends Block["type"]>(blockId: string, updates: BlockUpdate<T>) => void;
  onRemoveBlock: (blockId: string) => void;
  onReorderBlocks: (blockIds: string[]) => void;
}

// Map block types to icons
const blockIcons: Record<BlockType, React.ComponentType<{ className?: string }>> = {
  text: Type,
  heading: Heading,
  button: MousePointerClick,
  card: Square,
  section: LayoutList,
  spacer: Space,
  divider: Minus,
  banner: Megaphone,
  html: Code,
  "rich-text": Type,
  popup: MessageSquare,
  "appointment-list": Calendar,
  "order-list": Package,
  "chatroom-list": MessageCircle,
  "file-list": FileText,
  "form-response-list": ClipboardList,
  "pinned-forms-list": NotebookPen,
  "profile-edit": UserPen,
  "content-list": BookOpen,
  "purchase-list": CreditCard,
  "file-upload": Upload,
  "stripe-subscriptions": Zap,
  "stripe-billing-portal-button": Receipt,
  "stripe-invoices": Receipt,
  "vital-data": Activity,
  "appointment-booking": CalendarPlus,
  iframe: Frame,
};

// Map block types to display labels
const blockLabels: Record<BlockType, string> = {
  text: i18n.t("Text", { ns: "common" }),
  heading: i18n.t("Heading", { ns: "common" }),
  button: i18n.t("Button", { ns: "common" }),
  card: i18n.t("Card", { ns: "common" }),
  section: i18n.t("Section", { ns: "common" }),
  spacer: i18n.t("Spacer", { ns: "common" }),
  divider: i18n.t("Divider", { ns: "common" }),
  banner: i18n.t("Banner", { ns: "common" }),
  html: i18n.t("HTML", { ns: "common" }),
  "rich-text": i18n.t("Rich Text", { ns: "common" }),
  popup: i18n.t("Popup", { ns: "common" }),
  "appointment-list": i18n.t("Appointments", { ns: "common" }),
  "order-list": i18n.t("Orders", { ns: "common" }),
  "chatroom-list": i18n.t("Messages", { ns: "common" }),
  "file-list": i18n.t("Files", { ns: "common" }),
  "form-response-list": i18n.t("Form Responses", { ns: "common" }),
  "pinned-forms-list": i18n.t("Forms", { ns: "common" }),
  "profile-edit": i18n.t("Profile Edit", { ns: "common" }),
  "content-list": i18n.t("Content", { ns: "common" }),
  "purchase-list": i18n.t("Purchases", { ns: "common" }),
  "file-upload": i18n.t("File Upload", { ns: "common" }),
  "stripe-subscriptions": i18n.t("Stripe Subscriptions", { ns: "common" }),
  "stripe-billing-portal-button": i18n.t("Stripe Billing Portal", { ns: "common" }),
  "stripe-invoices": i18n.t("Stripe Invoices", { ns: "common" }),
  "vital-data": i18n.t("Vital Data", { ns: "common" }),
  "appointment-booking": i18n.t("Book Appointment", { ns: "common" }),
  iframe: i18n.t("Iframe", { ns: "common" }),
};

interface BlockItemProps {
  block: Block;
  isDragging: boolean;
  isExpanded: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onToggle: () => void;
  onRemove: () => void;
  onUpdate: (updates: Partial<Block>) => void;
}

const BlockItem: React.FC<BlockItemProps> = ({
  block,
  isDragging,
  isExpanded,
  onDragStart,
  onDragOver,
  onDragEnd,
  onToggle,
  onRemove,
  onUpdate,
}) => {
  const Icon = blockIcons[block.type];

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={cn("rounded border bg-background transition-opacity", isDragging && "opacity-50")}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 px-2 py-1.5">
        {/* Drag handle */}
        <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded">
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        {/* Clickable area for expand/collapse */}
        <div className="flex-1 flex items-center gap-2 cursor-pointer" onClick={onToggle}>
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="flex-1 text-sm font-medium">{blockLabels[block.type]}</span>
          <ChevronDown
            className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", isExpanded && "rotate-180")}
          />
        </div>
        {/* Delete button */}
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0 hover:bg-destructive/10 hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      {/* Expanded content */}
      {isExpanded && (
        <div className="px-2 pb-2 pt-2 border-t">
          <BlockEditor block={block} onChange={onUpdate} />
        </div>
      )}
    </div>
  );
};

// Helper to reorder array elements
function arrayMove<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...array];
  const removed = result.splice(fromIndex, 1)[0];
  if (removed !== undefined) {
    result.splice(toIndex, 0, removed);
  }
  return result;
}

export const BlockList: React.FC<BlockListProps> = ({
  blocks,
  onAddBlock,
  onUpdateBlock,
  onRemoveBlock,
  onReorderBlocks,
}) => {
  const { t } = useTranslation();
  const [blockSelectorOpen, setBlockSelectorOpen] = useState(false);
  const [expandedBlockId, setExpandedBlockId] = useState<string | null>(null);

  // Track which block is being dragged
  const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);

  const handleToggleBlock = (blockId: string) => {
    setExpandedBlockId((prev) => (prev === blockId ? null : blockId));
  };

  const handleDragStart = (blockId: string) => {
    setDraggedBlockId(blockId);
  };

  const handleDragOver = (e: React.DragEvent, targetBlockId: string) => {
    e.preventDefault();
    if (!draggedBlockId || draggedBlockId === targetBlockId) return;

    const fromIndex = blocks.findIndex((b) => b.id === draggedBlockId);
    const toIndex = blocks.findIndex((b) => b.id === targetBlockId);
    if (fromIndex === -1 || toIndex === -1) return;

    // Reorder immediately for smooth feedback
    const newOrder = arrayMove(blocks, fromIndex, toIndex);
    onReorderBlocks(newOrder.map((b) => b.id));
  };

  const handleDragEnd = () => {
    setDraggedBlockId(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">{t("Blocks")}</h3>
        <Button variant="ghost" size="sm" onClick={() => setBlockSelectorOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          {t("Add")}
        </Button>
      </div>

      {blocks.length > 0 ? (
        <div className="space-y-1.5">
          {blocks.map((block) => (
            <BlockItem
              key={block.id}
              block={block}
              isDragging={draggedBlockId === block.id}
              isExpanded={expandedBlockId === block.id}
              onDragStart={() => handleDragStart(block.id)}
              onDragOver={(e) => handleDragOver(e, block.id)}
              onDragEnd={handleDragEnd}
              onToggle={() => handleToggleBlock(block.id)}
              onRemove={() => onRemoveBlock(block.id)}
              onUpdate={(updates) => onUpdateBlock(block.id, { type: block.type, ...updates })}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-6 border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground mb-2">{t("No blocks yet")}</p>
          <Button variant="outline" size="sm" onClick={() => setBlockSelectorOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            {t("Add First Block")}
          </Button>
        </div>
      )}

      <BlockTypeSelector open={blockSelectorOpen} onOpenChange={setBlockSelectorOpen} onSelect={onAddBlock} />
    </div>
  );
};
